import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays';
import { Button } from '~/components/ui';
import { useDeactivateTenantUser } from '~/api/users';
import { extractGraphQLErrorMessages, type ClientError } from '~/utils';

export type TenantUserRowData = {
  id: string;
  firstName: string;
  lastName: string;
  employeeId?: string | null;
  phoneNumber?: string | null;
};

type Props = {
  user: TenantUserRowData | null;
  onClose: () => void;
};

export function DeactivateUserDialog({ user, onClose }: Props) {
  const { t } = useTranslation('users');
  const { mutate: deactivate, isPending } = useDeactivateTenantUser();

  if (!user) return null;

  const handleConfirm = () =>
    deactivate(
      { userId: user.id },
      {
        onSuccess: () => {
          toast.success(t('users.toast.updated'));
          onClose();
        },
        onError: (error: ClientError) =>
          toast.error(extractGraphQLErrorMessages(error).join(', ')),
      }
    );

  return (
    <Modal isOpen onClose={onClose}>
      <div className="bg-neut-bg-alt rounded-lg p-6 w-full max-w-sm">
        <h2
          id="Modal-title"
          className="text-base font-semibold text-neut-prim mb-2"
        >
          {t('users.confirmDeactivate.title')}
        </h2>
        <p className="text-sm text-neut-sec mb-4">
          {t('users.confirmDeactivate.body', {
            name: `${user.firstName} ${user.lastName}`,
          })}
        </p>
        <div className="flex gap-3">
          <Button
            size="sm"
            variant="accent"
            onClick={onClose}
            disabled={isPending}
          >
            {t('users.confirmDeactivate.cancel')}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={handleConfirm}
            loading={isPending}
          >
            {t('users.confirmDeactivate.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
